// client/guest/GuestFlow.tsx
import { useEffect, useMemo, useRef, useState } from "react";
import EntryQR from "../components/EntryQR";
import { api } from "../lib/api";
import {
  Step,
  normalizeStep,
  TicketKind,
  TicketSubkind,
  isKindDisabled,
  isSubDisabled,
  EventLite,
  lsKey,
  useOnboardingSync,
  fetchActiveEvents,
  fetchEventKinds,
  fetchPassStatuses,
} from "./utils";
import HomeScreen, {
  EventList,
  PassesList,
  PaymentBox,
} from "./components/HomeScreen";
import EulaBlock from "./components/EulaBlock";
import NoEventBlock from "./components/NoEventBlock";
import DoneBlock from "./components/DoneBlock";
import KindDetailsBlock from "./components/KindDetailsBlock";
import SubkindDetailsBlock from "./components/SubkindDetailsBlock";
import BottomNav from "./components/BottomNav";

/** Простая обёртка для единообразного каркаса экрана */
function GuestLayout({
  title,
  children,
  footer,
}: {
  title: string;
  children: React.ReactNode;
  footer?: React.ReactNode;
}) {
  const isIOS =
    typeof navigator !== "undefined" &&
    /iphone|ipad|ipod/i.test((navigator.userAgent || "").toLowerCase());

  // Дополнительный внешний отступ сверху для карточки на iOS (монобровь / Dynamic Island),
  // чтобы фон и скругления тоже опустились ниже системной шапки Telegram.
  const topInset = isIOS ? 90 : 0;
  

  return (
    <div
      className="w-full flex justify-center"
      // сам контейнер растягиваем на весь экран, отступ сверху остаётся
      style={{ paddingTop: topInset, minHeight: "100dvh" }}
    >
      <section
        className="ui-card relative w-full mx-auto max-w-xl flex flex-col overflow-hidden backdrop-blur-sm"
        // ВАЖНО: фиксируем высоту, а не minHeight, чтобы тёмный фон НЕ рос выше экрана
        style={{ height: `calc(100dvh - ${topInset + 32}px)` }}
      >
        {/* Header */}
        <header className="shrink-0 px-4 sm:px-5 pt-4 pb-3 flex items-center justify-between">
          <h1 className="text-[18px] font-semibold tracking-tight text-white/95">
            {title}
          </h1>
        </header>

        {/* Content */}
        <div className="px-4 sm:px-5 pb-20 flex-1 min-h-0 flex flex-col">
          {children}
        </div>

        {/* Footer */}
        {footer}
      </section>
    </div>
  );
}

export default function GuestFlow() {
  const [step, setStep] = useState<Step>("home");
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);

  // EULA
  const [eulaRequired, setEulaRequired] = useState(false);
  const [eulaText, setEulaText] = useState("");

  // event / tickets
  const [hasActiveEvent, setHasActiveEvent] = useState(false);
  const [eventId, setEventId] = useState<number | null>(null);
  const [currency, setCurrency] = useState<string>("₽");
  const [kinds, setKinds] = useState<TicketKind[]>([]);
  const [selectedKindId, setSelectedKindId] = useState<number | null>(null);
  const [selectedSubId, setSelectedSubId] = useState<number | null>(null);

  // promo / prices
  const [promoInput, setPromoInput] = useState<string>("");
  const [appliedPromo, setAppliedPromo] = useState<string | null>(null);
  const [finalPrice, setFinalPrice] = useState<number | null>(null);
  const [finalAmountForPay, setFinalAmountForPay] = useState<number | null>(
    null
  );
  const [promoLoading, setPromoLoading] = useState(false);
  const [promoError, setPromoError] = useState<string | null>(null);

  // toast
  const [toast, setToast] = useState<string | null>(null);
  const toastTimer = useRef<number | null>(null);

  // Home: есть ли вообще хоть один готовый пропуск
  const [homePassReady, setHomePassReady] = useState(false);

  // Events list (multi‑event)
  const [events, setEvents] = useState<EventLite[]>([]);
  const [eventsLoading, setEventsLoading] = useState(false);
  const [eventsError, setEventsError] = useState<string | null>(null);

  // Passes list (по всем мероприятиям)
  const [passes, setPasses] = useState<{ event: EventLite; step: Step | null }[]>(
    []
  );
  const [passesLoading, setPassesLoading] = useState(false);
  const [passesError, setPassesError] = useState<string | null>(null);

  // трекаем внешнюю оплату (чисто для UX)
  const tinkoffOpenedRef = useRef(false);

  // landing screen after returning from payment
  const [payLanding, setPayLanding] = useState<null | { status: "success" | "fail"; orderId?: string | null }>(null);
  const [payLandingCountdown, setPayLandingCountdown] = useState<number>(0);

  // Remember last selected event to restore after reload
  const lastEventRef = useRef<number | null>(null);

  useEffect(() => {
    try {
      const raw = window.localStorage.getItem("gf:last_event");
      const n = raw ? Number(raw) : NaN;
      lastEventRef.current = Number.isFinite(n) && n > 0 ? n : null;
    } catch {
      lastEventRef.current = null;
    }
  }, []);

  // Persist on change
  useEffect(() => {
    if (!eventId) return;
    try {
      window.localStorage.setItem("gf:last_event", String(eventId));
    } catch {}
  }, [eventId]);

  // Restore if eventId is not selected yet and events list already loaded
  useEffect(() => {
    if (eventId) return;
    const last = lastEventRef.current;
    if (!last) return;
    if (!events || events.length === 0) return;

    const exists = events.some((e) => Number(e.id) === Number(last));
    if (exists) setEventId(last);
  }, [events, eventId]);

  // Глобальный sync онбординга: только payment/pending → pass/done + флаг homePassReady
  // Теперь учитываем конкретный eventId, чтобы готовый пропуск по одному событию
  // не перебивал незавершённую оплату по другому.
  useOnboardingSync(api as any, step, eventId, setStep, setHomePassReady);

  // Reload event-specific data (kinds + eula) and re-sync onboarding when switching events.
  // This prevents "EULA accepted" state from bleeding from event #1 into event #2 in UI.
  useEffect(() => {
    if (!eventId) return;

    let alive = true;
    (async () => {
      try {
        setLoading(true);
        setErr(null);

        // Reset state that must NOT bleed between events
        setSelectedKindId(null);
        setSelectedSubId(null);
        setPromoInput("");
        setAppliedPromo(null);
        setFinalPrice(null);
        setFinalAmountForPay(null);
        setPromoError(null);

        const data = await fetchEventKinds(api as any, eventId);
        if (!alive) return;
        if (!data) {
          setErr("Не удалось загрузить данные мероприятия");
          // keep the user on home so they can re-select
          setStep("home");
          return;
        }

        setCurrency(data.currency || "₽");
        setKinds(data.kinds || []);
        setEulaText((data as any).eula_text || "");
        setEulaRequired(!!(data as any).eula_required);

        // IMPORTANT: onboarding is per-event; on event switch we must apply the correct step.
        let next: Step = (data as any).eula_required ? "eula" : "kinds";
        try {
          const ob: any = await (api as any).onboarding.get(eventId ?? undefined);
          const s = normalizeStep(ob?.step);
          if (s) next = s as Step;
        } catch {}

        if (!alive) return;
        setStep((prev) => (prev === "home" || prev === "passes" ? prev : next));
      } finally {
        if (alive) setLoading(false);
      }
    })();

    return () => {
      alive = false;
    };
  }, [eventId]);

  useEffect(() => {
    try {
      const u = new URL(window.location.href);
      const payStatus = (u.searchParams.get("pay_status") || "").toLowerCase();
      if (payStatus !== "success" && payStatus !== "fail") return;

      const orderId = u.searchParams.get("order_id");
      setPayLanding({ status: payStatus as any, orderId });

      const total = 5;
      setPayLandingCountdown(total);

      const iv = window.setInterval(() => {
        setPayLandingCountdown((x) => (x > 0 ? x - 1 : 0));
      }, 1000);

      const tm = window.setTimeout(() => {
        try {
          u.searchParams.delete("pay_status");
          u.searchParams.delete("order_id");
          window.history.replaceState({}, "", u.toString());
        } catch {}

        setPayLanding(null);
        setStep("home");
      }, total * 1000);

      return () => {
        window.clearInterval(iv);
        window.clearTimeout(tm);
      };
    } catch {}
}, []);

  // --- START TINKOFF PAYMENT PATCH ---
  function buildReturnUrl() {
    try {
      // Return back into the miniapp after payment
      const u = new URL(window.location.href);
      return u.toString();
    } catch {
      return undefined;
    }
  }

  function openExternal(url: string) {
    const tg = (window as any)?.Telegram?.WebApp;

    // IMPORTANT:
    // User wants the payment page to open in the same Mini App webview (not in Safari/Chrome).
    // The most reliable way is to navigate the current window.
    try {
      // If we are inside Telegram WebApp, navigate the current webview.
      // This usually keeps user inside Telegram (same miniapp webview), unlike tg.openLink
      // which may open an external browser depending on platform.
      if (tg) {
        window.location.href = url;
        return true;
      }
    } catch {
      // ignore and try fallbacks
    }

    // Fallbacks (dev browser or if navigation is blocked)
    try {
      window.location.assign(url);
      return true;
    } catch {
      // Telegram APIs as a fallback (may open external browser)
      try {
        if (tg?.openLink) {
          tg.openLink(url);
          return true;
        }
        if (tg?.openTelegramLink) {
          tg.openTelegramLink(url);
          return true;
        }
      } catch {}

      try {
        window.open(url, "_blank", "noopener,noreferrer");
        return true;
      } catch {
        return false;
      }
    }
  }

  async function startTinkoffPayment() {
    try {
      const amountToPay = (finalAmountForPay ?? finalPrice) as any;

      // IMPORTANT:
      // optionId = kind (event option)
      // suboptionId = subkind (event suboption)
      const kindIdToPay = (selectedKindId ?? null) as any;
      const subkindIdToPay = (selectedSubId ?? null) as any;

      console.log("[startTinkoffPayment] create payload", {
        eventId,
        optionId: kindIdToPay,
        suboptionId: subkindIdToPay,
        amount: amountToPay,
      });

      const payload: any = {
        optionId: kindIdToPay,
        suboptionId: subkindIdToPay || undefined,
        amount: amountToPay,
        promo_code: appliedPromo ?? (promoInput.trim() || undefined),
        eventId: eventId ?? undefined,
        returnUrl: buildReturnUrl(),
      };

      const result: any = await (api as any).payments.tinkoffCreate(payload);

      // server returns { ok: true, payment_url, payment_id }
      const payUrl = result?.payment_url || result?.paymentUrl || result?.url || null;
      if (!result?.ok || !payUrl) {
        console.error("[startTinkoffPayment] bad response", result);
        throw new Error("Не удалось создать платёж");
      }

      // Update onboarding state before leaving the app
      try {
        await (api as any).onboarding.set("pending", eventId ?? undefined);
      } catch {
        // not fatal
      }

      tinkoffOpenedRef.current = true;

      const opened = openExternal(String(payUrl));
      if (!opened) {
        throw new Error("Не удалось открыть страницу оплаты");
      }

      // While user is in external browser, keep UI in waiting state
      setStep("pending");

      return result;
    } catch (e) {
      console.error("[startTinkoffPayment] create failed (raw)", e);
      throw e;
    }
  }
  // --- END PATCH ---

  /* --------------------------- TOASTS --------------------------- */
  useEffect(() => {
    const handler = (e: any) => {
      const msg = String(e?.detail || "");
      if (!msg) return;
      setToast(msg);
      if (toastTimer.current) window.clearTimeout(toastTimer.current);
      toastTimer.current = window.setTimeout(() => {
        setToast(null);
        toastTimer.current = null;
      }, 3000) as unknown as number;
    };
    window.addEventListener("toast", handler as any);
    return () => {
      window.removeEventListener("toast", handler as any);
      if (toastTimer.current) window.clearTimeout(toastTimer.current);
    };
  }, []);

  /* ------------------------ BOOTSTRAP -------------------------- */
  useEffect(() => {
    let cancelled = false;

    const bootstrap = async () => {
      try {
        setLoading(true);
        setErr(null);
        let anyReady = false;

        // 1) EULA
        try {
          const eula: any = await (api as any).eula.status();
          if (!cancelled && eula) {
            setEulaRequired(!!eula.required);
            setEulaText(String(eula.text || ""));
          }
        } catch {
          // не фатально
        }

        // 2) Список событий
        let evs: EventLite[] = [];
        try {
          setEventsLoading(true);
          evs = await fetchActiveEvents(api as any);
          if (!cancelled) {
            setEvents(evs);
            setHasActiveEvent(evs.length > 0);
          }
        } catch (e: any) {
          if (!cancelled) {
            setEventsError(e?.message || "Не удалось загрузить события");
            setEvents([]);
            setHasActiveEvent(false);
          }
        } finally {
          setEventsLoading(false);
        }

        // 3) Статусы пропусков
        try {
          setPassesLoading(true);
          const st = await fetchPassStatuses(api as any, evs);
          if (!cancelled) {
            setPasses(st);
            anyReady = st.some(
              (p) => p.step === "pass" || p.step === "done"
            );
            setHomePassReady(anyReady);
          }
        } catch (e: any) {
          if (!cancelled) {
            setPassesError(e?.message || "Не удалось загрузить статусы пропусков");
            setPasses([]);
          }
        } finally {
          setPassesLoading(false);
        }

        if (!cancelled) {
          // Если вернулись после успешной оплаты и уже есть готовый пропуск,
          // сразу открываем экран пропусков; иначе — обычный "home".
          let initialStep: Step = "home";
          try {
            if (typeof window !== "undefined") {
              const params = new URLSearchParams(window.location.search || "");
              const from = params.get("from");
              if (from === "tinkoff_success" && anyReady) {
                initialStep = "passes";
              }
            }
          } catch {
            // игнорируем ошибки парсинга URL
          }
          setStep(initialStep);
        }
      } catch (e: any) {
        if (!cancelled) {
          setErr(e?.message || "Ошибка инициализации");
          setStep("error");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    bootstrap();
    return () => {
      cancelled = true;
    };
  }, []);

  /* ----------------- LOCAL STORAGE: билет ---------------------- */
  useEffect(() => {
    if (!eventId) {
      setSelectedKindId(null);
      setSelectedSubId(null);
      return;
    }
    try {
      const kKey = lsKey(eventId, "kind");
      const sKey = lsKey(eventId, "sub");
      if (kKey) {
        const v = window.localStorage.getItem(kKey);
        if (v) setSelectedKindId(Number(v) || null);
      }
      if (sKey) {
        const v = window.localStorage.getItem(sKey);
        if (v) setSelectedSubId(Number(v) || null);
      }
    } catch {
      // ignore
    }
  }, [eventId]);

  useEffect(() => {
    if (!eventId) return;
    try {
      const kKey = lsKey(eventId, "kind");
      const sKey = lsKey(eventId, "sub");
      if (kKey) {
        if (selectedKindId)
          window.localStorage.setItem(kKey, String(selectedKindId));
        else window.localStorage.removeItem(kKey);
      }
      if (sKey) {
        if (selectedSubId)
          window.localStorage.setItem(sKey, String(selectedSubId));
        else window.localStorage.removeItem(sKey);
      }
    } catch {
      // ignore
    }
  }, [eventId, selectedKindId, selectedSubId]);

  /* ------------------ DERIVED TICKET STATE --------------------- */
  const currentKind: TicketKind | null = useMemo(
    () => kinds.find((k) => k.id === selectedKindId) || null,
    [kinds, selectedKindId]
  );
  const currentSub: TicketSubkind | null = useMemo(
    () =>
      currentKind?.subkinds?.find((s) => s.id === selectedSubId) || null,
    [currentKind, selectedSubId]
  );

  // базовая цена выбранного билета
  useEffect(() => {
    const price = currentSub?.price_rub ?? currentKind?.price_rub ?? null;
    setFinalPrice(price);
    setFinalAmountForPay(price);
    setAppliedPromo(null);
    setPromoError(null);
  }, [currentKind, currentSub]);

  /* --------------------------- LOADERS -------------------------- */
  async function loadEventById(id: number) {
    const res = await fetchEventKinds(api as any, id);
    if (!res) throw new Error("Событие не найдено");
    setEventId(res.eventId);
    setCurrency(res.currency || "₽");
    setKinds(res.kinds);
  }

  async function reloadEvents() {
    try {
      setEventsLoading(true);
      setEventsError(null);
      const evs = await fetchActiveEvents(api as any);
      setEvents(evs);
      setHasActiveEvent(evs.length > 0);
    } catch (e: any) {
      setEventsError(e?.message || "Не удалось загрузить события");
      setEvents([]);
      setHasActiveEvent(false);
    } finally {
      setEventsLoading(false);
    }
  }

  async function reloadPassStatuses() {
    try {
      setPassesLoading(true);
      setPassesError(null);
      const st = await fetchPassStatuses(api as any, events);
      setPasses(st);
      const anyReady = st.some((p) => p.step === "pass" || p.step === "done");
      setHomePassReady(anyReady);
    } catch (e: any) {
      setPassesError(e?.message || "Не удалось загрузить статусы пропусков");
      setPasses([]);
    } finally {
      setPassesLoading(false);
    }
  }

  /* ------------------------- NAV HELPERS ------------------------ */
  function goHome() {
    // Домой всегда ведёт на основной экран с кнопками
    setStep("home");
  }

  function handleBack() {
    switch (step) {
      case "event":
      case "passes":
      case "noevent":
        goHome();
        break;
      case "eula":
      case "kinds":
        setStep("event");
        break;
      case "subkind":
        setStep("kinds");
        break;
      case "payment":
      case "pending":
        setStep("kinds");
        break;
      case "pass":
      case "done":
        setStep("passes");
        break;
      default:
        goHome();
        break;
    }
  }

  /* ------------------------ HOME ACTIONS ------------------------ */
  const handleHomeBuyClick = async () => {
    setStep("event");
    if (!events.length) {
      await reloadEvents();
    }
  };

  const handleHomePassClick = async () => {
    setStep("passes");
    await reloadPassStatuses();
  };

  /* --------------------- EVENT SELECTION FLOW ------------------- */
  const handleEventSelect = async (id: number) => {
    try {
      setLoading(true);
      setErr(null);

      await loadEventById(id);

      // Берём состояние онбординга только для этого event_id
      let ob: any = null;
      try {
        ob = await (api as any).onboarding.get(id);
      } catch {
        ob = null;
      }
      const s = normalizeStep(ob?.step);

      if (s === "pass" || s === "done") {
        setStep("pass");
      } else if (eulaRequired && (!s || s === "eula")) {
        setStep("eula");
      } else {
        setStep("kinds");
      }
    } catch (e: any) {
      setErr(e?.message || "Не удалось открыть мероприятие");
      setStep("error");
    } finally {
      setLoading(false);
    }
  };

  /* --------------------------- EULA ----------------------------- */
  async function acceptEula()   {
    try {
      // Persist onboarding progress for this specific event
      await (api as any).onboarding.set("kinds", eventId ?? undefined);
    } catch {}

    // reflect locally as well
    setEulaRequired(false);
    setStep("kinds");
  }

  /* -------------------------- PROMO ----------------------------- */
  async function activatePromo() {
    try {
      setPromoLoading(true);
      setPromoError(null);
      const optionId = selectedSubId ?? selectedKindId;
      if (!optionId) {
        setPromoError("Сначала выберите билет");
        return;
      }
      if (finalPrice == null) {
        setPromoError("Не найдена цена билета");
        return;
      }

      const r = await fetch("/api/promo/preview", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          eventId,
          amount: finalPrice,
          promo_code: promoInput,
        }),
      });
      const data: any = await r.json().catch(() => ({}));
      const disc = Number(data?.discount ?? 0);

      if (!r.ok || !data?.ok || !(disc > 0)) {
        setAppliedPromo(null);
        setFinalAmountForPay(finalPrice);
        setPromoError("Промокод не найден или не активен");
        return;
      }

      setAppliedPromo(String(data.promo_code || promoInput).toUpperCase());
      setFinalAmountForPay(Number(data.finalAmount ?? 0));
    } catch {
      setPromoError("Сеть недоступна");
    } finally {
      setPromoLoading(false);
    }
  }

  // /* -------------------------- PAYMENT --------------------------- */
  // async function startTinkoffPayment() {
  //   try {
  //     const optionId = selectedSubId ?? selectedKindId;

  //     if (!eventId || !optionId) {
  //       setToast("Сначала выберите билет");
  //       return;
  //     }
  //     if (finalAmountForPay == null) {
  //       setToast("Не найдена сумма оплаты");
  //       return;
  //     }

  //     tinkoffOpenedRef.current = true;

  //     const data: any = await (api as any).payments.tinkoffCreate({
  //       optionId,
  //       amount: finalAmountForPay,
  //       promo_code: appliedPromo || promoInput || undefined,
  //     });

  //     if (!data?.ok) {
  //       throw new Error(data?.error || "Не удалось создать платёж");
  //     }

  //     const paymentUrl: string | null =
  //       data.paymentUrl || data.paymentURL || data.PaymentURL || null;

  //     if (paymentUrl) {
  //       window.location.href = paymentUrl;
  //     } else {
  //       setStep("pending");
  //     }
  //   } catch (e: any) {
  //     console.error("[startTinkoffPayment] error", e);
  //     setErr(e?.message || "Ошибка создания платежа");
  //   }
  // }

  /* --------------------------- PAY ------------------------------ */
  async function payWithTinkoff() {
    try {
      const optionId = selectedSubId ?? selectedKindId;
      if (!optionId) {
        window.dispatchEvent(
          new CustomEvent("toast", { detail: "Сначала выберите билет" })
        );
        return;
      }
      if (promoLoading) return;

      const origin = (globalThis.location?.origin || "").replace(/\/$/, "");
      const successUrl = `${origin}/api/pay/success`;
      const failUrl = `${origin}/api/pay/fail`;

      const payload: any = { optionId, successUrl, failUrl };
      if (appliedPromo || promoInput.trim()) {
        payload.promo_code = (appliedPromo || promoInput).trim();
      }

      window.dispatchEvent(
        new CustomEvent("toast", { detail: "Готовим оплату…" })
      );
      const r: any = await (api as any).tinkoff
        .create(payload)
        .catch((e: any) => ({
          ok: false,
          error: e?.message || "CREATE_FAILED",
        }));

      if (!r?.ok) {
        const reason =
          r?.error || r?.reason || "Не удалось создать ссылку оплаты";
        window.dispatchEvent(new CustomEvent("toast", { detail: reason }));
        return;
      }

      const url = r?.paymentUrl || r?.payment_url || r?.url || null;
      const amountRub = Number(
        r?.amount_rub ?? finalAmountForPay ?? finalPrice ?? 0
      );

      // Нулевой платёж – остаёмся на экране оплаты, ждём, пока сервер подтянет готовый пропуск
      if (!url || amountRub === 0) {
        window.dispatchEvent(
          new CustomEvent("toast", {
            detail: "Заявка отправлена. Ожидаем подтверждение…",
          })
        );
        setStep("payment");
        return;
      }

      let opened = false;
      try {
        const tg = (window as any)?.Telegram?.WebApp;
        if (tg?.openLink) {
          tg.openLink(String(url), { try_browser: true });
          opened = true;
        }
      } catch {}

      if (!opened) {
        try {
          globalThis.location!.href = String(url);
          opened = true;
        } catch {}
      }
      if (!opened) {
        try {
          window.open(String(url), "_blank");
          opened = true;
        } catch {}
      }

      if (opened) {
        tinkoffOpenedRef.current = true;
        setStep("payment");
      } else {
        window.dispatchEvent(
          new CustomEvent("toast", {
            detail: "Не удалось открыть страницу оплаты",
          })
        );
      }
    } catch {
      window.dispatchEvent(
        new CustomEvent("toast", { detail: "Ошибка оплаты" })
      );
    }
  }

  /* --------------------------- PASS ----------------------------- */
  async function openPassForEvent(id: number) {
    try {
      await loadEventById(id);
      setStep("pass");
    } catch (e: any) {
      setErr(e?.message || "Не удалось открыть пропуск");
      setStep("error");
    }
  }

  // выдача конкретного QR происходит внутри EntryQR, поэтому сюда передаём только onBack
  const passScreen = (
    <GuestLayout
      title="Гостевой вход"
      footer={<BottomNav onBack={handleBack} onHome={goHome} hidden={step !== "pass"} />}
    >
      <EntryQR
        hidden={step !== "pass"}
        eventId={eventId}
        onBack={() => setStep("passes")}
        onHome={goHome}
      />
    </GuestLayout>
  );

    /* --------------------------- RENDER --------------------------- */

  const footer = (
    <BottomNav
      onBack={handleBack}
      onHome={goHome}
      // hidden={step === "home"}
    />
  );

  let body: React.ReactNode;

  if (loading && step === "home") {
    body = (
      <div className="flex-1 flex items-center justify-center text-sm text-white/70">
        Загружаем…
      </div>
    );
  } else if (err && step === "error") {
    body = (
      <div className="flex-1 flex flex-col items-center justify-center gap-3 text-sm text-red-200">
        <div>Ошибка: {err}</div>
        <button
          className="rounded-xl bg-white/10 px-3 py-1.5 text-white/90"
          onClick={() => window.location.reload()}
        >
          Перезагрузить
        </button>
      </div>
    );
  } else {
    switch (step) {
      case "home":
        body = (
          <HomeScreen
            homePassReady={homePassReady}
            onBuyClick={handleHomeBuyClick}
            onPassClick={handleHomePassClick}
          />
        );
        break;

      case "event":
        body = (
          <EventList
            events={events}
            loading={eventsLoading}
            error={eventsError}
            onRetry={reloadEvents}
            onSelect={handleEventSelect}
          />
        );
        break;

      case "passes":
        body = (
          <PassesList
            items={passes}
            loading={passesLoading}
            error={passesError}
            onRetry={reloadPassStatuses}
            onOpenQR={openPassForEvent}
          />
        );
        break;

      case "eula":
        body = (
          <EulaBlock
            required={eulaRequired}
            text={eulaText}
            onAccept={acceptEula}
          />
        );
        break;

      case "kinds":
        body = (
          <KindDetailsBlock
            eventId={eventId}
            kinds={kinds}
            selectedKindId={selectedKindId}
            currency={currency}
            onSelectKind={setSelectedKindId}
            onNext={() => {
              const k = kinds.find((kk) => kk.id === selectedKindId) || null;
              if (k && k.subkinds && k.subkinds.length > 0) {
                setStep("subkind");
              } else {
                setStep("payment");
              }
            }}
          />
        );
        break;

    case "subkind":
      body = (
        <SubkindDetailsBlock
          eventId={eventId}
          kind={currentKind}
          selectedSubId={selectedSubId}
          currency={currency}
          onSelectSub={setSelectedSubId}
          onNext={() => setStep("payment")}
          onBack={() => setStep("kinds")}
          onHome={goHome}
        />
      );
      // footer = (
      //   <BottomNav
      //     onBack={handleBack}
      //     onHome={goHome}
      //   />
      // );
      break;

      case "payment":
      case "pending":
        body = (
          <PaymentBox
            promoInput={promoInput}
            setPromoInput={setPromoInput}
            appliedPromo={appliedPromo}
            promoLoading={promoLoading}
            promoError={promoError}
            finalAmountForPay={finalAmountForPay}
            currency={currency}
            onActivatePromo={activatePromo}
            onPay={startTinkoffPayment}
          />
        );
        break;

      case "pass":
        // передаём eventId, чтобы /entry/issue не падал на активное событие
        body = <EntryQR eventId={eventId} />;
        break;

      case "done":
        body = <DoneBlock />;
        break;

      case "noevent":
        body = <NoEventBlock />;
        break;

      default:
        body = (
          <div className="flex-1 flex items-center justify-center text-sm text-white/70">
            Неизвестный шаг: {String(step)}
          </div>
        );
    }
  }

  if (payLanding) {
    const ok = payLanding.status === "success";
    return (
      <GuestLayout title="Оплата">
        <div className="flex-1 min-h-0 flex flex-col items-center justify-center gap-3 text-center">
          <div className={`text-[18px] font-semibold ${ok ? "text-emerald-200" : "text-red-200"}`}>
            {ok ? "Оплата прошла успешно" : "Оплата не прошла"}
          </div>

          {/* {payLanding.orderId ? (
            <div className="text-xs text-white/60">Заказ: {payLanding.orderId}</div>
          ) : null} */}

          <div className="text-sm text-white/75">
            {ok
              ? "Сейчас мы вернём вас в приложение и подготовим пропуск."
              : "Вернём вас в приложение. Попробуйте оплатить ещё раз."}
          </div>

          <div className="mt-2 rounded-xl border border-white/10 bg-white/5 px-3 py-2 text-xs text-white/70">
            Возврат на главную через {payLandingCountdown || 5} сек…
          </div>
        </div>
      </GuestLayout>
    );
  }

  return (
    <GuestLayout title="Гостевой вход" footer={footer}>
      {body}

      {toast && (
        <div className="fixed left-0 right-0 bottom-4 flex justify-center px-4">
          <div className="max-w-md rounded-2xl bg-black/80 text-white text-sm px-3 py-2">
            {toast}
          </div>
        </div>
      )}
    </GuestLayout>
  );
}
